import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Mine, or everyone's.
 *
 * 🔴 THIS IS A MECHANISM BEING SURFACED, NOT ONE BEING INVENTED. Both halves of
 * the pattern already ship: the loans list has exactly this segmented control
 * ("My loans" / "All loans", persisted per user), and the buyers index has the
 * same capability buried as an "Everyone" option inside a `<select>` of staff
 * names. The capability is not the problem — its VISIBILITY is. A dropdown
 * option is a thing you find after you already suspect it exists; the question
 * "is this my queue or the whole book" is the first one a reader asks on a
 * list, so its answer belongs where they are already looking, in a control that
 * states the current answer without being opened.
 *
 * WHY A SEGMENTED CONTROL RATHER THAN A CHIP. The two scopes are exhaustive and
 * mutually exclusive — a row is in the book whether or not it is mine, so
 * "mine" is a lens, not a filter that can be combined with another. Chips read
 * as additive; a two-segment control reads as a switch, which is what it is.
 * That is also why this is NOT `FilterChips` with two chips.
 *
 * ACCESSIBILITY. Two real `<button>`s in a labelled `role="group"`, each
 * carrying `aria-pressed`. The active state is never colour alone — the pressed
 * segment is announced as pressed and is also the bolder of the two.
 *
 * PERSISTENCE, ROUTING AND THE DEFAULT SCOPE ARE THE CALLER'S. This holds no
 * state and reads no session: which scope a page opens on is a product
 * decision, and localStorage keys are a page's business.
 */

export type ScopeValue = "mine" | "all";

export interface ScopeToggleProps {
  value: ScopeValue;
  onChange: (next: ScopeValue) => void;
  /** e.g. "My buyers". Name the records, not the scope. */
  mineLabel: string;
  /** e.g. "All buyers". */
  allLabel: string;
  /** Optional counts. Shown only when BOTH are known — one number alone
   *  invites the reader to compare it with a blank. */
  mineCount?: number;
  allCount?: number;
  /** Accessible name for the pair, e.g. "Buyer view". */
  label: string;
  className?: string;
  "data-testid"?: string;
}

export function ScopeToggle({
  value,
  onChange,
  mineLabel,
  allLabel,
  mineCount,
  allCount,
  label,
  className,
  "data-testid": testId,
}: ScopeToggleProps) {
  const showCounts = mineCount != null && allCount != null;
  return (
    <div
      className={cn(
        "inline-flex overflow-hidden rounded-md border border-border-light",
        className,
      )}
      role="group"
      aria-label={label}
      data-testid={testId}
    >
      <ScopeSegment
        active={value === "mine"}
        onClick={() => onChange("mine")}
        count={showCounts ? mineCount : undefined}
        testId={testId ? `${testId}-mine` : undefined}
      >
        {mineLabel}
      </ScopeSegment>
      <ScopeSegment
        active={value === "all"}
        onClick={() => onChange("all")}
        count={showCounts ? allCount : undefined}
        testId={testId ? `${testId}-all` : undefined}
      >
        {allLabel}
      </ScopeSegment>
    </div>
  );
}

function ScopeSegment({
  active,
  onClick,
  count,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  count?: number;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-space-1 px-space-3 py-space-1 text-small",
        "transition-colors duration-fast ease-standard",
        active
          ? "bg-onyx font-semibold text-white-pure"
          : "bg-white-pure text-onyx hover:bg-white-warm",
      )}
      data-testid={testId}
    >
      {children}
      {count != null && (
        <span
          className={cn(
            "rounded-pill px-space-1 text-[10px] font-bold tabular-nums",
            active ? "bg-white-pure/20 text-white-pure" : "bg-white-warm text-gray-mid",
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}
